import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import "./yomiri-logo.css";

interface YomiriLogoProps {
  size?: "small" | "medium" | "large" | number;
  onClick?: () => void;
  markOnly?: boolean;
  animated?: boolean;
  mode?: "default" | "loading";
  className?: string;
}

let hasPlayedGlobalEntrance = false;

export function YomiriLogo({
  size = "medium",
  markOnly = false,
  animated = false,
  mode = "default",
  onClick,
  className = "",
}: YomiriLogoProps) {
  const shouldAnimate = mode === "loading" && animated;

  const numericSize =
    typeof size === "number"
      ? size
      : size === "large"
      ? 40
      : size === "small"
      ? 24
      : 30;

  const fontSize =
    typeof size === "number"
      ? `${size * 0.045}rem`
      : size === "large"
      ? "1.45rem"
      : size === "small"
      ? "1.05rem"
      : "1.25rem";

  const isInteractive = Boolean(onClick);
  const isLoading = mode === "loading";

  const animationClass = isLoading
    ? "yomiri-logo-loading"
    : shouldAnimate
    ? "yomiri-logo-animate"
    : "";

  return (
    <Box
      onClick={onClick}
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      aria-label={onClick ? "Yomiri, ir al inicio" : "Yomiri"}
      className={`yomiri-logo ${isInteractive ? "yomiri-logo-interactive" : ""} ${className}`}
      sx={{
        p: 0,
        m: 0,
        border: 0,
        background: "transparent",
        width: "fit-content",
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        flexShrink: 0,
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <svg
        className={`yomiri-logo-svg ${animationClass}`}
        width={numericSize}
        height={numericSize}
        viewBox="0 0 512 512"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          flexShrink: 0,
          transformOrigin: "center center",
        }}
      >
        {/* Soft Liquid Glass Squircle Container */}
        <rect
          width="512"
          height="512"
          rx="124"
          fill="var(--y-container, #0d0d14)"
        />
        {/* Constructing Border Frame */}
        <rect
          className="yomiri-logo-border"
          x="18"
          y="18"
          width="476"
          height="476"
          rx="106"
          stroke="var(--accent-500, #ff2e63)"
          strokeWidth="22"
        />
        {/* Constructing Stylized Book / Y Branches */}
        <path
          className="yomiri-logo-v"
          d="M132 172L256 280L380 172"
          stroke="var(--text-primary, #ffffff)"
          strokeWidth="46"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Constructing Vertical Stem */}
        <path
          className="yomiri-logo-stem"
          d="M256 280V400"
          stroke="var(--text-primary, #ffffff)"
          strokeWidth="46"
          strokeLinecap="round"
        />
        {/* Cherry Accent Sphere with Radiant Bloom */}
        <circle
          className="yomiri-logo-cherry"
          cx="256"
          cy="128"
          r="34"
          fill="var(--accent-500, #ff2e63)"
        />
      </svg>

      {!markOnly && (
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontWeight: 800,
            fontSize,
            letterSpacing: "0.06em",
            color: "var(--text-primary, #ffffff)",
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "baseline",
          }}
        >
          YOMIRI
          <span
            style={{
              color: "var(--accent-500, #ff2e63)",
              marginLeft: "1px",
              textShadow: "0 0 10px rgba(255, 46, 99, 0.6)",
            }}
          >
            .
          </span>
        </span>
      )}
    </Box>
  );
}
